// Closing-gate Bypass confirmation modal.
//
// Mirrors AdminUndoConfirmModal: an action-specific consequence paragraph, a
// free-text "Reason (audit-visible)" textarea validated to at least 5
// characters (matching the backend 400 guard so the round-trip never happens
// on the happy path), and a destructive CONFIRM: BYPASS button so an admin
// closing the modal by reflex can't fire it. The reason field is REQUIRED.

import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

export const BYPASS_MIN_REASON_LENGTH = 5;

export interface BypassConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** Title shown in the modal chrome. */
  title?: string;
  /** Action-specific paragraph describing what the bypass does. */
  consequence?: ReactNode;
  /** Fired with the trimmed reason when the admin confirms. */
  onConfirm: (reason: string) => void | Promise<void>;
  /** Drives the loading state of the Confirm button + locks the modal. */
  isPending: boolean;
}

const DEFAULT_CONSEQUENCE = (
  <>
    This force-completes the Escrow Setup gate for a <strong>TEST</strong> deal:
    it stamps issuance + funding-verified <em>without any real, funded escrow
    close</em>, so Underwriting Section&nbsp;2, Closing Step&nbsp;3, and the
    escrow half of the boarding gate all read as complete. Use it only on test
    deals — never a live one.
  </>
);

export function BypassConfirmModal({
  open,
  onClose,
  title = "Bypass escrow gate (test)",
  consequence = DEFAULT_CONSEQUENCE,
  onConfirm,
  isPending,
}: BypassConfirmModalProps) {
  const [reason, setReason] = useState("");

  // Reset on close so the next open starts fresh.
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= BYPASS_MIN_REASON_LENGTH;
  const showLengthHint = reason.length > 0 && !reasonValid;

  const handleConfirm = () => {
    if (!reasonValid || isPending) return;
    void onConfirm(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={isPending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reasonValid || isPending}
            loading={isPending}
          >
            CONFIRM: BYPASS
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="font-semibold text-status-warning" role="alert">
          ⚠ This action is audit-logged and visible to compliance reviewers.
        </p>
        <div className="text-small text-gray-dark">{consequence}</div>

        <label
          htmlFor="gate-bypass-reason"
          className="block mt-space-2 text-small font-display uppercase tracking-label text-gold-shadow"
        >
          Reason for bypass (audit-visible)
        </label>
        <textarea
          id="gate-bypass-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          placeholder={`Explain why this bypass is necessary. Min. ${BYPASS_MIN_REASON_LENGTH} characters.`}
          rows={3}
          disabled={isPending}
          aria-invalid={showLengthHint || undefined}
          aria-describedby={showLengthHint ? "gate-bypass-reason-hint" : undefined}
        />
        {showLengthHint && (
          <p
            id="gate-bypass-reason-hint"
            className="text-caption text-status-error"
            role="alert"
          >
            Reason must be at least {BYPASS_MIN_REASON_LENGTH} characters.
          </p>
        )}
      </div>
    </Modal>
  );
}
